import React, { Component } from "react";
import { Button } from "react-bootstrap-buttons";
import DatePicker, { registerLocale } from "react-datepicker";
import { Formik, Form, Field, ErrorMessage } from "formik";
import enGB from "date-fns/locale/en-GB";
import "react-datepicker/dist/react-datepicker.css";
import CustomInputDate from "./CustomInputDate";
import CustomInputTime from "./CustomInputTime";
import moment from "moment-timezone";
import { dtzWithLocalZone, dtLocalToDtz } from "../utils/utils";
import * as api from "../utils/api";
import { validateTextField, renderFieldError } from "../utils/validation";
import * as actionCreators from "../modules/actions";
import { getFavoriteStations } from "../modules/actions";
import { connect } from "react-redux";
import RecurrenceSelector from "./RecurrenceSelector";
import CustomSwitch from "./CustomSwitch";
import CustomAttributeToggle from "./CustomAttributeToggle";
import EventTypeFields from "./EventTypeFields";
import Select from "react-select";
import countryList from "react-select-country-list";

/**Register the locale to display calendar in UK format, week starting with Monday */
registerLocale("en-GB", enGB);
/**Store the folder path for webpack created images folder */
const images = require.context("../assests/images", true);
const today = new Date();
const defaultRecurrence = {
  startDate: today,
  endDate: today,
  frequency: "weekly",
  numberOfRepetitions: 1,
  weekDaysRepetition: [],
  endingOccurrencesNumber: 0,
  isAllDay: false,
  startTime: today,
  endTime: today,
  layout: 'horizontal',
};

const extraCountries = [
  { value: "Europe", label: "Europe" },
  { value: "England", label: "England" },
  { value: "Scotland", label: "Scotland" },
  { value: "Wales", label: "Wales" },
  { value: "Northern Ireland", label: "Northern Ireland" },
];

const sortedCountryOptions = (() => {
  const base = countryList().getData().map(({ label }) => ({ value: label, label }));;
  const merged = base.concat(extraCountries);
  merged.sort((a, b) => a.label.localeCompare(b.label));
  return merged;
})();

function emptyEvent(timezone) {
  return {
    name: "",
    start_time: moment().tz(timezone).add(2, "hour").startOf("hour"),
    end_time: moment().tz(timezone).add(3, "hour").startOf("hour"),
    occurrences: "daily",
    description: "",
    recurring: false,
    recurrence_start_time: moment().tz(timezone).add(2, "hour").startOf("hour"),
    recurrence_end_time: moment().tz(timezone).add(3, "hour").startOf("hour"),
    days: [],
    interval: 1,
    ending_occurrences_number: null,
    stations: [],
    country: "",
  };
}

/**Component displayed as a modal popup with input for title, description and event date/time for custom event creation
 * @param cancelModal : Cancel Modal when clicked outside
 * @param evtForm : Form element with event title and description input fields
 * @param moment = prop for date picker element for default date
 * @param handleDateSelect = prop for date picker for handling date selection
 * @param onChange = Handler for event title and desc onChange
 * @param createEvent = Handler for create event button click
 *
 * Imported compoenent DatePicker for the date selection feature
 * CustomInputDate sent as prop to DatePicker to render the customizated
 * input text field with calender icon at right
 */
class Eventpopup extends Component {
  constructor(props) {
    super(props);

    let formDataObj = emptyEvent(props.timezone);
    const id = props?.meetupObj?.event?.id || null;
    const incomingRecurring = props.meetupObj?.event?.recurring ?? props.meetupObj?.recurring ?? false;
    formDataObj = {
      ...formDataObj,
      edit: (id !== null),
      update_mode: props.editMode || '1',
    };
    
    // if we're cloning an existing fixture, override just the bits we need
    if (props.meetupObj) {
      const meetupObj = props.meetupObj.event ? props.meetupObj.event : props.meetupObj;
      const start_time = meetupObj.time ? meetupObj.time : meetupObj.start_time;
      const end_time = meetupObj.end_time;
      const mo_start = moment(start_time).tz(props.timezone);
      const mo_end = end_time ? moment(end_time).tz(props.timezone) : mo_start;

      const description = meetupObj.title ? meetupObj.title : meetupObj.description;
      const event_type = meetupObj.event_type ? meetupObj.event_type : "Sport";
      const event_sub_type = meetupObj.sport ? meetupObj.sport : meetupObj.event_sub_type;

      formDataObj = {
        ...formDataObj,
        event_type: event_type,
        event_sub_type: event_sub_type || "",
        name: meetupObj.name,
        start_time: mo_start,
        end_time: mo_end,

        stations: (props.meetupObj.stations || []).map(s => s.id),
        description: description || "",
        country: meetupObj.country || "International",
        recurring: incomingRecurring,
      };
      if ( props.meetupObj.event ) {
        const e = props.meetupObj.event;
        formDataObj.recurrence_start_time     = e.recurrence_start_time ? moment(e.recurrence_start_time).tz(props.timezone) : formDataObj.recurrence_start_time;
        formDataObj.recurrence_end_time       = e.recurrence_end_time ? moment(e.recurrence_end_time).tz(props.timezone) : formDataObj.recurrence_end_time;
        formDataObj.days                      = e.days || [];
        formDataObj.interval                  = e.interval || 1;
        formDataObj.ending_occurrences_number = e.ending_occurrences_number;
      }
    }
    this.state = {
      eventForm: formDataObj,
      isRecurring: incomingRecurring,
    };
    this.handleEventTypeChange = this.handleEventTypeChange.bind(this);
  }
  componentDidMount() {
    this.props.getFavoriteStations();
  }
  onSuccess = () => {
    // Only call handleSuccess if the parent provided it
    if (typeof this.props.handleSuccess === "function") {
      this.props.handleSuccess();
    }
    this.props.showNotice("The custom event was successfully created", true);

    if (typeof this.props.cancelModal === "function") {
      this.props.cancelModal();
    }

    // Always show a notice
    this.props.showNotice(
      "The custom event was successfully " + (this.props.meetupObj ? "saved" : "created"),
      true
    );

    // Optionally auto‐close the modal
    if (typeof this.props.cancelModal === "function") {
      this.props.cancelModal();
    }
  };


  onError = (err) => {
    const msg =
      err?.response?.data?.message    // “proper” API error
      || err.message                  // generic JS error
      || "An unexpected error occurred";
    this.props.showNotice(msg, false);
    // close the modal if you like:
    if (typeof this.props.cancelModal === "function") {
      this.props.cancelModal();
    }
  };

  /**Creates a new custom event based on user input
   */
  createCustomEvent = () => {
    const eventObj = { ...this.state.eventForm };
    api.endpoints.createCustomEvent(
        this.props.locationId,
        eventObj,
        this.onSuccess,
        this.onError
    );
  };

  editCustomEvent = () => {
    const eventObj = { ...this.state.eventForm };
    api.endpoints.editCustomEvent(
        this.props.locationId,
        this.props.meetupObj.event.id,
        eventObj,
        this.onSuccess,
        this.onError,
        this.props.editMode
    );
  };

  /**Cancels the create custom event modal
   */
  cancelModal = () => {
    if (typeof this.props.cancelModal === 'function') {
      this.props.cancelModal();
    } else if (typeof this.props.onClose === 'function') {
      this.props.onClose();
    }
  };

  /**OnChange handler for fields in custom event form
   *@param fieldvalue= value,
   *@param field= identifier to identify the input element on the form.
   */
  changeHandler = (field, fieldValue) => {
    const eventObj = { ...this.state.eventForm };
    eventObj[field] = fieldValue;
    if (field === "start_time") {
      const d = fieldValue.toObject();
      eventObj["end_time"].set({
        year: d.years,
        month: d.months + 1,
        day: d.date,
        hour: d.hours + 1,
        minute: d.minutes,
      });
    }
    this.setState({
      eventForm: eventObj,
    });
  };

  handleChangeValue = (field, fieldValue, name) => {
    this.changeHandler(field, fieldValue);
    return name;
  };
  /** if event_type changes, force recurring off when sport */
  handleEventTypeChange(field, value) {
    if (field === "event_type") {
      const isSport = (value || "").toLowerCase() === "sport";

      this.setState(prev => ({
        eventForm: {
          ...prev.eventForm,
          event_type: value,
          ...(isSport ? { recurring: false } : {}),
        },
        ...(isSport ? { isRecurring: false } : {}),
      }));
    } else {
      this.setState(prev => ({
        eventForm: { ...prev.eventForm, [field]: value }
      }));
    }
  }

  render() {
    const { eventForm, isRecurring } = this.state;
    const isSport = (eventForm.event_type || "").toLowerCase() === "sport";

    // swap labels
    const eventTitleLabel = isSport ? "Fixture" : "Event Title";
    const eventDescriptionLabel = isSport ? "Competition name" : "Event description";
    const eventDescriptionPlaceholder = isSport ? "Please type the name of the competition" : "Please type the name of the event";
    const subTypeLabel = isSport ? "Sport type" : "Event sub type";
    return (
        <div>
          <div className="modal-backdrop bg-secondary opacity6" />
          <div className="modal d-inline-block">
            <div className="modal-dialog-centered modal__custom-event modal__custom-event--dialog-width">
              <div className="modal-content pl-5 pr-5 pt-3 pb-3  m-auto bg--gray1">
                <Formik
                    enableReinitialize={true}
                    validateOnChange={true}
                    validateOnBlur={true}
                    validate={values => {
                      const errors = {};
                      if (!values.event_date) {
                        errors.event_date = "Event date is required";
                      } else if (moment(values.event_date).isBefore(moment(), "day")) {
                        errors.event_date = "Event date cannot be in the past";
                      }
                      if (!values.start_time){
                        errors.start_time = "Event time is required";
                      } else {
                        const dt = moment(values.event_date)
                          .hour(moment(values.start_time).hour())
                          .minute(moment(values.start_time).minute());
                        if (dt.isBefore(moment())) {
                          errors.start_time = "Event time cannot be in the past";
                        }
                      }
                      if (!values.event_type) {
                        errors.event_type = "Event type is required";
                      }
                      if (!values.event_sub_type) {
                        errors.event_sub_type = `${subTypeLabel} is required`;
                      }
                      if (!values.event_title) {
                        errors.event_title = `${eventTitleLabel} is required`;
                      }
                      if (!values.description) {
                        errors.description = `${eventDescriptionLabel} is required`;
                      }
                      return errors;
                    }}

                    initialValues={{
                      start_time: this.state.eventForm.start_time.toDate() === undefined || "" ? "" : this.state.eventForm.start_time.toDate(),
                      end_time: this.state.eventForm.end_time.toDate() === undefined || "" ? "" : this.state.eventForm.end_time.toDate(),
                      event_date:
                          this.state.eventForm.start_time.toDate() === undefined || "" ? "" : this.state.eventForm.start_time.toDate(),
                      event_title:
                          this.state.eventForm.name === undefined || "" ? "" : this.state.eventForm.name,
                      recurring: this.state.eventForm.recurring || "",
                      recurrence_start_time: this.state.eventForm.recurrence_start_time.toDate(),
                      recurrence_end_time: this.state.eventForm.recurrence_end_time.toDate(),
                      days: this.state.eventForm.days,
                      interval: this.state.eventForm.interval,
                      ending_occurrences_number: this.state.eventForm.ending_occurrences_number,
                      event_type: this.state.eventForm.event_type || "",
                      event_sub_type: this.state.eventForm.event_sub_type || "",
                      sport_type: this.state.eventForm.sport_type || "",
                      team1: this.state.eventForm.team1 || "",
                      team2: this.state.eventForm.team2 || "",
                      stations: this.state.eventForm.stations || "",
                      description: this.state.eventForm.description || "",
                      country:     this.state.eventForm.country     || "",
                    }}
                    onSubmit={(values) => {
                      if (this.state.eventForm.edit){
                        this.editCustomEvent();
                      }else{
                        this.createCustomEvent();
                      }
                    }}
                >
                  {({
                      values,
                      errors,
                      touched,
                      submitForm,
                      isValid,
                      isSubmitting,
                      setFieldValue,
                      setFieldTouched,
                  }) => (
                      <Form>
                        <div className="modal-header pl-0 py-3">
                          <div className="d-flex">
                            { !isSport && (
                              <div className="d-flex pt-2 flex-row col-1 border border-left-0 border-top-0 border-bottom-0">
                                <CustomAttributeToggle
                                  text="Recurring"
                                  enabled={isRecurring}
                                  switchClass="feature-switch"
                                  onToggle={(checked) => {
                                    this.setState({ isRecurring: checked });
                                    this.changeHandler("recurring", checked);
                                  }}
                                />
                              </div>
                            )}
                            <div className="d-flex flex-column col-6 border border-left-0 border-top-0 border-bottom-0">
                              <label className="fs-body2">Event Date</label>
                              <DatePicker
                                  selected={dtzWithLocalZone(
                                      this.state.eventForm.start_time
                                  )}
                                  dateFormat="dd/MM/yyyy"
                                  strictParsing={true}
                                  name="event_date"
                                  onChange={(date) => {
                                    if (date !== null) {
                                      const date_tz = dtLocalToDtz(
                                          date,
                                          this.props.timezone
                                      );
                                      return setFieldValue(
                                          this.handleChangeValue(
                                              "start_time",
                                              date_tz,
                                              "event_date"
                                          ),
                                          date_tz.toDate().toDateString()
                                      );
                                    }
                                  }}
                                  locale="en-GB"
                                  useWeekdaysShort={true}
                                  monthsShown={1}
                                  customInput={
                                    <CustomInputTime style={{ minWidth: "63px !important" }} />
                                  }

                              />
                              {errors.event_date && touched.event_date && (
                                <div className="text-danger">{errors.event_date}</div>
                              )}
                            </div>
                            <div className="d-flex flex-column col-4 border border-left-0 border-top-0 border-bottom-0">
                              <label className="fs-body2">Event Time</label>
                              <div className="d-flex col-11 p-0" style={{ maxHeight: "45px !important" }} >
                                <DatePicker
                                    name="start_time"
                                    selected={dtzWithLocalZone(
                                        this.state.eventForm.start_time
                                    )}
                                    dateFormat={
                                      this.props.time_format === "24H"
                                          ? "HH:mm"
                                          : "hh:mm aa"
                                    }
                                    onChange={(date) => {
                                      if (date !== null) {
                                        const date_tz = dtLocalToDtz(
                                            date,
                                            sessionStorage.getItem("timezone")
                                        );
                                        this.changeHandler("start_time", date_tz);
                                      }
                                    }}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeCaption=""
                                    timeFormat={
                                      this.props.time_format === "24H"
                                          ? "HH:mm"
                                          : "hh:mm aa"
                                    }
                                    customInput={
                                      <CustomInputTime style={{ minWidth: "63px" }} />
                                    }
                                />

                                <div className="p-2" />
                                <DatePicker
                                    name="end_time"
                                    selected={dtzWithLocalZone(
                                        this.state.eventForm.end_time
                                    )}
                                    dateFormat={
                                      this.props.time_format === "24H"
                                          ? "HH:mm"
                                          : "hh:mm aa"
                                    }
                                    onChange={(date) => {
                                      if (date !== null) {
                                        this.changeHandler(
                                            "end_time",
                                            dtLocalToDtz(date, this.props.timezone)
                                        );
                                      }
                                    }}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeCaption=""
                                    timeFormat={
                                      this.props.time_format === "24H"
                                          ? "HH:mm"
                                          : "hh:mm aa"
                                    }
                                      customInput={
                                        <CustomInputTime style={{ minWidth: "60px !important" }} />
                                      }
                                />
                              </div>
                              {errors.start_time  && touched.start_time  && (
                                  <div className="text-danger">{errors.start_time }</div>
                              )}
                            </div>
                            <div className="pr-3 pl-0 pt-3 col-1 pl-4 ">
                              <img
                                  src={images("./img/custom-event.svg")}
                                  alt="event"
                                  className="icon-img-size"
                              />
                            </div>
                          </div>
                          <div className="d-flex">
                            <div></div>
                          </div>
                        </div>
                        <div className="modal-body px-0 ml-0 bg--gray1">
                          {this.state.isRecurring && (
                              <RecurrenceSelector
                                  recurrence={defaultRecurrence}
                                  eventForm={this.state.eventForm}
                                  timezone={this.props.timezone}
                                  onChange={(field, value) => {
                                    this.changeHandler(field, value);
                                  }}
                              />
                          )}
                          <EventTypeFields
                            eventForm={this.state.eventForm}
                            onChange={(field, value) => {
                              this.handleEventTypeChange(field, value);
                              setFieldValue(field, value);
                            }}
                            favoriteStations={this.props.favoriteStations}
                          />
                          <div className="row form-group mb-3">
                            <div className="col-12 flex-column">
                              <label className="fs-body2">{eventTitleLabel}</label>
                              <Field
                                  type="text"
                                  name="event_title"
                                  className="form-control"
                                  placeholder={`Your ${(eventTitleLabel || "").toLowerCase()} name`}
                                  value={this.state.eventForm.name}
                                  validate={validateTextField}
                                  onChange={(event) => {
                                    setFieldValue(
                                        this.handleChangeValue(
                                            "name",
                                            event.target.value,
                                            "event_title"
                                        ),
                                        event.target.value
                                    );
                                  }}
                              />
                              {errors.event_title   && touched.event_title   && (
                                  <div className="text-danger">{errors.event_title  }</div>
                              )}
                            </div>
                          </div>
                          {isSport && (
                            <>
                              {/* swap description to input */}
                              <div className="row form-group mb-3">
                                <div className="col-12 flex-column">
                                  <label className="fs-body2">{eventDescriptionLabel}</label>
                                  <Field
                                    type="text"
                                    name="description"
                                    className="form-control"
                                    placeholder={`Your ${(eventDescriptionPlaceholder || "").toLowerCase()}`}
                                    value={values.description}
                                    onChange={e => {
                                      setFieldValue("description", e.target.value);
                                      this.changeHandler("description", e.target.value);
                                    }}
                                  />
                                  {errors.description && touched.description && (
                                    <div className="text-danger">{errors.description}</div>
                                  )}
                                </div>
                              </div>

                              {/* new Country dropdown */}
                              <div className="row form-group mb-3">
                                <div className="col-12 flex-column">
                                  <label className="fs-body2">Country</label>
                                  <Select
                                    name="country"
                                    options={[
                                      { value: "International", label: "International" },
                                      ...sortedCountryOptions
                                    ]}
                                    value={sortedCountryOptions.find(c => c.value === values.country) ||
                                        { value: values.country, label: values.country }}
                                    onChange={opt => {
                                      const val = opt ? opt.value : "";
                                      setFieldValue("country", val);
                                      this.changeHandler("country", val);
                                    }}
                                    isSearchable
                                    placeholder="Select country…"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                          {!isSport && (
                            <div className="row form-group">
                              <div className="col-12 flex-column">
                                <label className="fs-body2">{eventDescriptionLabel}</label>
                                <Field
                                  as="textarea"
                                  name="description"
                                  rows="6"
                                  className="form-control align-top"
                                  placeholder={`Your ${(eventDescriptionLabel || "").toLowerCase()}`}
                                  value={values.description}
                                  onChange={e => {
                                    setFieldValue("description", e.target.value);
                                    this.changeHandler("description", e.target.value);
                                  }}
                                />
                                <ErrorMessage
                                  name="description"
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </div>
                          )}

                        </div>
                        <div className="modal-footer p-0 border-0">
                          <Button
                              type="button"
                              className="button-warning--modal p-1 mr-2"
                              onClick={this.cancelModal}
                          >
                            Cancel
                          </Button>
                          <Button
                              type="submit"
                              className="button-success--modal p-1"
                              onClick={submitForm}
                              disabled={isSubmitting}
                          >
                            {this.state.eventForm.edit ? "Update event" : "Create event"}
                          </Button>
                        </div>
                      </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    showNotice: (message, success) =>
        dispatch(actionCreators.showNotice(message, success)),
    getFavoriteStations,
  };
};

const mapStateToProps = (state) => ({
  favoriteStations: state.favoriteStations,
});

export default connect(mapStateToProps, mapDispatchToProps)(Eventpopup);
